import { useState, type FormEvent } from 'react';
import {
  Ban,
  CheckCircle2,
  Clock,
  FileText,
  IndianRupee,
  Package,
  Search,
  Send,
  Truck,
} from 'lucide-react';

import {
  Badge,
  Button,
  Card,
  Drawer,
  EmptyState,
  ErrorAlert,
  Field,
  Input,
  PageHeader,
  Select,
  Skeleton,
  StatCard,
  Table,
  Td,
  Textarea,
  Th,
} from '@/components/ui';
import { humanize, statusTone } from '@/lib/status';
import {
  ORDER_STATUS_LABEL,
  ORDER_TRANSITIONS,
  PAYMENT_STATUS_LABEL,
  SHIPMENT_STATUS_LABEL,
  useCancelOrder,
  useChangeOrderStatus,
  useCreateShipment,
  useGenerateInvoice,
  useOrder,
  useOrderPayments,
  useOrders,
  useOrderSummary,
  useRecordOrderPayment,
  type OrderStatus,
} from '@/lib/orders-api';
import {
  cn,
  errorMessage,
  fieldErrors,
  formatCompactCurrency,
  formatCurrency,
  formatDateTime,
  formatNumber,
} from '@/lib/utils';

/**
 * Order administration (spec §33).
 *
 * The status control offers only the moves the state machine allows from where
 * the order actually is, and every one of them is re-checked server-side. What
 * a transition *costs* — committing reserved stock, holding or releasing
 * deposit — is decided there too, in the same transaction as the status row, so
 * nothing on this screen has to know or restate it.
 */

const STATUS_TABS: Array<{ label: string; value: OrderStatus[] | null }> = [
  { label: 'All', value: null },
  { label: 'Awaiting payment', value: ['PENDING_PAYMENT'] },
  { label: 'Confirmed', value: ['CONFIRMED'] },
  { label: 'Processing', value: ['PROCESSING', 'READY_TO_SHIP'] },
  { label: 'Shipped', value: ['SHIPPED', 'IN_TRANSIT'] },
  { label: 'Delivered', value: ['DELIVERED', 'COMPLETED'] },
  { label: 'Cancelled', value: ['CANCELLED', 'REJECTED'] },
];

type ActionKind = 'status' | 'cancel' | 'payment' | 'shipment';

const ACTION_TITLE: Record<ActionKind, string> = {
  status: 'Move this order on',
  cancel: 'Cancel this order',
  payment: 'Record a payment',
  shipment: 'Dispatch a consignment',
};

export function OrdersPage() {
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState(0);
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // `STATUS_TABS[0]` is a literal in this file, so the fallback always resolves;
  // the non-null assertion states that rather than threading undefined onward.
  const activeTab = STATUS_TABS[tab] ?? STATUS_TABS[0]!;

  const orders = useOrders({
    page,
    limit: 20,
    search,
    status: activeTab.value ?? undefined,
  });
  const summary = useOrderSummary();

  return (
    <div>
      <PageHeader
        title="Order Management"
        description="Track, confirm, invoice and dispatch bulk orders."
      />

      {/* --- Counters ------------------------------------------------------- */}
      <div className="mb-5 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {summary.isLoading ? (
          [0, 1, 2, 3].map((index) => <Skeleton key={index} className="h-24" />)
        ) : (
          <>
            <StatCard
              label="Awaiting payment"
              value={formatNumber(summary.data?.pending ?? 0)}
              icon={<Clock className="size-5" aria-hidden="true" />}
              tone="warning"
            />
            <StatCard
              label="In the warehouse"
              value={formatNumber(
                (summary.data?.confirmed ?? 0) + (summary.data?.processing ?? 0),
              )}
              icon={<Package className="size-5" aria-hidden="true" />}
              tone="brand"
            />
            <StatCard
              label="Shipped"
              value={formatNumber(summary.data?.shipped ?? 0)}
              icon={<Truck className="size-5" aria-hidden="true" />}
              tone="brand"
            />
            <StatCard
              label="Order value"
              value={formatCompactCurrency(summary.data?.totalRevenue ?? 0)}
              icon={<IndianRupee className="size-5" aria-hidden="true" />}
              tone="success"
            />
          </>
        )}
      </div>

      {/* --- Filters -------------------------------------------------------- */}
      <Card className="mb-4 p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative min-w-56 flex-1">
            <Search
              className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-subtle"
              aria-hidden="true"
            />
            <Input
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
              placeholder="Search order number, company or customer code"
              className="pl-9"
              aria-label="Search orders"
            />
          </div>

          <div className="flex flex-wrap gap-1.5" role="tablist" aria-label="Order status">
            {STATUS_TABS.map((entry, index) => (
              <button
                key={entry.label}
                type="button"
                role="tab"
                aria-selected={tab === index}
                onClick={() => {
                  setTab(index);
                  setPage(1);
                }}
                className={cn(
                  'rounded-lg px-3 py-1.5 text-sm font-medium transition-colors',
                  tab === index
                    ? 'bg-brand-600 text-white'
                    : 'text-ink-muted hover:bg-canvas hover:text-ink',
                )}
              >
                {entry.label}
              </button>
            ))}
          </div>
        </div>
      </Card>

      {/* --- Table ---------------------------------------------------------- */}
      <Card>
        {orders.isLoading ? (
          <div className="space-y-2 p-4">
            {[0, 1, 2, 3, 4].map((index) => (
              <Skeleton key={index} className="h-12" />
            ))}
          </div>
        ) : orders.isError ? (
          <div className="p-4">
            <ErrorAlert title="Could not load orders">{errorMessage(orders.error)}</ErrorAlert>
          </div>
        ) : (orders.data?.items.length ?? 0) === 0 ? (
          <EmptyState
            title="No orders match"
            description="Try a different status or clear the search."
          />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Order</Th>
                <Th>Customer</Th>
                <Th>Status</Th>
                <Th>Payment</Th>
                <Th className="text-right">Quantity</Th>
                <Th className="text-right">Total</Th>
                <Th>Owner</Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {orders.data?.items.map((order) => (
                <tr key={order.id} className="hover:bg-canvas/60">
                  <Td>
                    <p className="font-medium text-ink">{order.orderNumber}</p>
                    <p className="text-xs text-ink-subtle">{formatDateTime(order.placedAt)}</p>
                  </Td>
                  <Td>
                    <p className="text-ink">{order.customer?.companyName ?? '—'}</p>
                    <p className="text-xs text-ink-subtle">{order.customer?.customerCode}</p>
                  </Td>
                  <Td>
                    <Badge tone={statusTone(order.status)}>
                      {ORDER_STATUS_LABEL[order.status]}
                    </Badge>
                  </Td>
                  <Td>
                    <Badge tone={statusTone(order.paymentStatus)}>
                      {PAYMENT_STATUS_LABEL[order.paymentStatus]}
                    </Badge>
                  </Td>
                  <Td className="text-right tabular-nums">
                    {formatNumber(order.totalQuantity)}
                    <span className="block text-xs text-ink-subtle">
                      {order.itemCount} {order.itemCount === 1 ? 'line' : 'lines'}
                    </span>
                  </Td>
                  <Td className="text-right font-medium tabular-nums">
                    {formatCurrency(order.totalAmount)}
                  </Td>
                  <Td className="text-sm text-ink-muted">{order.assignedTo?.name ?? 'Unassigned'}</Td>
                  <Td className="text-right">
                    <Button variant="outline" size="sm" onClick={() => setSelectedId(order.id)}>
                      Manage
                    </Button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}

        {orders.data && orders.data.pagination.totalPages > 1 && (
          <div className="flex items-center justify-between gap-3 border-t border-line px-4 py-3">
            <p className="text-sm text-ink-subtle">
              {orders.data.pagination.total} orders · page {orders.data.pagination.page} of{' '}
              {orders.data.pagination.totalPages}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={!orders.data.pagination.hasPreviousPage}
                onClick={() => setPage((current) => current - 1)}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={!orders.data.pagination.hasNextPage}
                onClick={() => setPage((current) => current + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>

      <OrderDrawer orderId={selectedId} onClose={() => setSelectedId(null)} />
    </div>
  );
}

/* ==========================================================================
   Detail drawer
   ========================================================================== */

function OrderDrawer({ orderId, onClose }: { orderId: string | null; onClose: () => void }) {
  const detail = useOrder(orderId);
  const payments = useOrderPayments(orderId);
  const generateInvoice = useGenerateInvoice();
  const [action, setAction] = useState<ActionKind | null>(null);

  const order = detail.data;
  const nextStatuses = order ? ORDER_TRANSITIONS[order.status] : [];

  return (
    <Drawer
      open={Boolean(orderId)}
      onClose={() => {
        setAction(null);
        onClose();
      }}
      title={order?.orderNumber ?? 'Order'}
    >
      {detail.isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-24" />
          <Skeleton className="h-40" />
        </div>
      ) : detail.isError ? (
        <ErrorAlert title="Could not load this order">{errorMessage(detail.error)}</ErrorAlert>
      ) : (
        order && (
          <div className="space-y-5">
            {/* --- Position ------------------------------------------------- */}
            <section>
              <div className="mb-3 flex flex-wrap items-center gap-2">
                <Badge tone={statusTone(order.status)}>{ORDER_STATUS_LABEL[order.status]}</Badge>
                <Badge tone={statusTone(order.paymentStatus)}>
                  {PAYMENT_STATUS_LABEL[order.paymentStatus]}
                </Badge>
                <Badge tone="neutral">{humanize(order.fulfillmentStatus)}</Badge>
              </div>

              <p className="text-sm text-ink">
                {order.customer?.companyName ?? 'Unknown customer'}
                {order.customer?.customerCode && (
                  <span className="text-ink-subtle"> · {order.customer.customerCode}</span>
                )}
              </p>
              {order.customer?.email && (
                <p className="text-xs text-ink-subtle">{order.customer.email}</p>
              )}
              <p className="mt-1 text-xs text-ink-subtle">
                Placed {formatDateTime(order.placedAt)}
              </p>

              {order.status === 'CANCELLED' && order.cancellationReason && (
                <p className="mt-2 rounded-lg bg-danger-50 px-3 py-2 text-sm text-danger-700">
                  Cancelled: {order.cancellationReason}
                </p>
              )}
            </section>

            {/* --- Money --------------------------------------------------- */}
            <section>
              <dl className="grid grid-cols-2 gap-3 rounded-lg border border-line p-4 text-sm">
                {(
                  [
                    ['Subtotal', order.subtotal],
                    ['GST', order.taxAmount],
                    ['Shipping', order.shippingAmount],
                    ['Discount', order.discountAmount],
                    ['Deposit applied', order.securityDepositAdjustment],
                    ['Order total', order.totalAmount],
                  ] as Array<[string, number]>
                ).map(([label, value]) => (
                  <div key={label}>
                    <dt className="text-xs text-ink-subtle">{label}</dt>
                    <dd className="font-medium tabular-nums text-ink">{formatCurrency(value)}</dd>
                  </div>
                ))}
              </dl>

              {payments.data && (
                <p
                  className={cn(
                    'mt-2 rounded-lg px-3 py-2 text-sm',
                    payments.data.outstandingAmount > 0
                      ? 'bg-warning-50 text-warning-800'
                      : 'bg-success-50 text-success-700',
                  )}
                >
                  {payments.data.outstandingAmount > 0
                    ? `${formatCurrency(payments.data.paidAmount)} received · ${formatCurrency(
                        payments.data.outstandingAmount,
                      )} outstanding.`
                    : `Paid in full — ${formatCurrency(payments.data.paidAmount)} received.`}
                </p>
              )}
            </section>

            {/* --- Actions -------------------------------------------------- */}
            {action ? (
              <ActionForm
                kind={action}
                order={order}
                outstanding={payments.data?.outstandingAmount ?? order.totalAmount}
                onDone={() => setAction(null)}
              />
            ) : (
              <section>
                <h3 className="mb-2 text-sm font-semibold text-ink">Actions</h3>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    disabled={nextStatuses.length === 0}
                    onClick={() => setAction('status')}
                    title={
                      nextStatuses.length === 0
                        ? 'This order has reached a terminal status.'
                        : undefined
                    }
                  >
                    <CheckCircle2 className="size-4" aria-hidden="true" />
                    Change status
                  </Button>
                  <Button variant="outline" onClick={() => setAction('payment')}>
                    <IndianRupee className="size-4" aria-hidden="true" />
                    Record payment
                  </Button>
                  <Button variant="outline" onClick={() => setAction('shipment')}>
                    <Send className="size-4" aria-hidden="true" />
                    Dispatch
                  </Button>
                  <Button
                    variant="danger"
                    disabled={!nextStatuses.includes('CANCELLED')}
                    onClick={() => setAction('cancel')}
                    title={
                      nextStatuses.includes('CANCELLED')
                        ? undefined
                        : 'Once goods have left the warehouse this comes back as a return, not a cancellation.'
                    }
                  >
                    <Ban className="size-4" aria-hidden="true" />
                    Cancel
                  </Button>

                  {order.invoice ? (
                    <p className="col-span-2 rounded-lg bg-canvas px-3 py-2 text-xs text-ink-muted">
                      Invoice {order.invoice.invoiceNumber} · {humanize(order.invoice.status)}
                    </p>
                  ) : (
                    <Button
                      variant="outline"
                      className="col-span-2"
                      disabled={generateInvoice.isPending}
                      onClick={() => generateInvoice.mutate({ orderId: order.id })}
                    >
                      <FileText className="size-4" aria-hidden="true" />
                      Raise GST invoice
                    </Button>
                  )}
                </div>

                {generateInvoice.isError && (
                  <div className="mt-2">
                    <ErrorAlert title="Could not raise the invoice">
                      {errorMessage(generateInvoice.error)}
                    </ErrorAlert>
                  </div>
                )}
              </section>
            )}

            {/* --- Items ---------------------------------------------------- */}
            <section>
              <h3 className="mb-2 text-sm font-semibold text-ink">
                Items ({order.items.length})
              </h3>
              <ul className="space-y-2">
                {order.items.map((item) => (
                  <li key={item.id} className="rounded-lg border border-line p-3 text-sm">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-medium text-ink">{item.productName}</p>
                        <p className="text-xs text-ink-subtle">
                          {item.variantName && `${item.variantName} · `}
                          {item.productSku}
                          {item.hsnCode && ` · HSN ${item.hsnCode}`}
                        </p>
                        <p className="mt-1 text-xs text-ink-muted">
                          {formatNumber(item.quantity)} × {formatCurrency(item.unitPrice)} ·{' '}
                          {item.taxPercent}% GST
                        </p>
                      </div>
                      <p className="shrink-0 font-medium tabular-nums text-ink">
                        {formatCurrency(item.lineTotal)}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            </section>

            {/* --- Addresses ------------------------------------------------ */}
            <section className="grid gap-3 sm:grid-cols-2">
              {(
                [
                  ['Ship to', order.shippingAddress],
                  ['Bill to', order.billingAddress],
                ] as const
              ).map(([label, address]) => (
                <div key={label} className="rounded-lg border border-line p-3 text-sm">
                  <p className="text-xs font-medium uppercase tracking-wide text-ink-subtle">
                    {label}
                  </p>
                  {address ? (
                    <address className="mt-1.5 not-italic text-ink-muted">
                      <span className="font-medium text-ink">{address.contactName}</span>
                      <br />
                      {address.addressLine1}
                      <br />
                      {address.city}, {address.state} {address.pincode}
                      {address.gstin && (
                        <>
                          <br />
                          <span className="text-xs">GSTIN: {address.gstin}</span>
                        </>
                      )}
                    </address>
                  ) : (
                    <p className="mt-1.5 text-ink-subtle">Not recorded.</p>
                  )}
                </div>
              ))}
            </section>

            {order.notes && (
              <section>
                <h3 className="mb-1 text-sm font-semibold text-ink">Buyer notes</h3>
                <p className="whitespace-pre-wrap rounded-lg bg-canvas p-3 text-sm text-ink-muted">
                  {order.notes}
                </p>
              </section>
            )}

            {/* --- Shipments ------------------------------------------------ */}
            {order.shipments.length > 0 && (
              <section>
                <h3 className="mb-2 text-sm font-semibold text-ink">Shipments</h3>
                <ul className="space-y-2">
                  {order.shipments.map((shipment) => (
                    <li key={shipment.id} className="rounded-lg border border-line p-3 text-sm">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="font-medium text-ink">{shipment.shipmentNumber}</p>
                          <p className="text-xs text-ink-subtle">
                            {shipment.carrier}
                            {shipment.trackingNumber && ` · ${shipment.trackingNumber}`}
                          </p>
                        </div>
                        <Badge tone={statusTone(shipment.status)}>
                          {SHIPMENT_STATUS_LABEL[shipment.status]}
                        </Badge>
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* --- History -------------------------------------------------- */}
            <section>
              <h3 className="mb-2 text-sm font-semibold text-ink">Status history</h3>
              {order.statusHistory.length === 0 ? (
                <p className="rounded-lg border border-dashed border-line px-4 py-6 text-center text-sm text-ink-subtle">
                  Nothing recorded yet.
                </p>
              ) : (
                <ol className="space-y-2">
                  {order.statusHistory.map((entry) => (
                    <li key={entry.id} className="rounded-lg border border-line p-3 text-sm">
                      <p className="font-medium text-ink">
                        {ORDER_STATUS_LABEL[entry.toStatus]}
                        {entry.fromStatus && (
                          <span className="font-normal text-ink-subtle">
                            {' '}
                            from {ORDER_STATUS_LABEL[entry.fromStatus]}
                          </span>
                        )}
                      </p>
                      {entry.notes && <p className="mt-1 text-xs text-ink-muted">{entry.notes}</p>}
                      <p className="mt-1 text-xs text-ink-subtle">
                        {formatDateTime(entry.createdAt)}
                        {entry.changedBy && ` · ${entry.changedBy}`}
                      </p>
                    </li>
                  ))}
                </ol>
              )}
            </section>
          </div>
        )
      )}
    </Drawer>
  );
}

/* ==========================================================================
   One form, shared by every action
   ========================================================================== */

function ActionForm({
  kind,
  order,
  outstanding,
  onDone,
}: {
  kind: ActionKind;
  order: { id: string; status: OrderStatus };
  outstanding: number;
  onDone: () => void;
}) {
  const changeStatus = useChangeOrderStatus();
  const cancel = useCancelOrder();
  const recordPayment = useRecordOrderPayment();
  const createShipment = useCreateShipment();

  const mutation =
    kind === 'status'
      ? changeStatus
      : kind === 'cancel'
        ? cancel
        : kind === 'payment'
          ? recordPayment
          : createShipment;

  const allowed = ORDER_TRANSITIONS[order.status].filter((next) => next !== 'CANCELLED');

  const [status, setStatus] = useState<string>(allowed[0] ?? '');
  const [notes, setNotes] = useState('');
  const [reason, setReason] = useState('');
  const [amount, setAmount] = useState(String(outstanding || ''));
  const [reference, setReference] = useState('');
  const [method, setMethod] = useState('Bank transfer');
  const [carrier, setCarrier] = useState('');
  const [trackingNumber, setTrackingNumber] = useState('');
  const [trackingUrl, setTrackingUrl] = useState('');
  const [estimatedDeliveryDate, setEstimatedDeliveryDate] = useState('');

  const errors = fieldErrors(mutation.error);

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    const onSuccess = () => onDone();

    if (kind === 'status') {
      changeStatus.mutate(
        { orderId: order.id, status: status as OrderStatus, notes: notes || undefined },
        { onSuccess },
      );
      return;
    }

    if (kind === 'cancel') {
      cancel.mutate({ orderId: order.id, reason }, { onSuccess });
      return;
    }

    if (kind === 'payment') {
      recordPayment.mutate(
        {
          orderId: order.id,
          amount: Number(amount),
          reference: reference || undefined,
          method: method || undefined,
          notes: notes || undefined,
        },
        { onSuccess },
      );
      return;
    }

    createShipment.mutate(
      {
        orderId: order.id,
        carrier,
        trackingNumber: trackingNumber || undefined,
        trackingUrl: trackingUrl || undefined,
        estimatedDeliveryDate: estimatedDeliveryDate || undefined,
        notes: notes || undefined,
      },
      { onSuccess },
    );
  };

  return (
    <section className="rounded-lg border border-line p-4">
      <h3 className="mb-3 text-sm font-semibold text-ink">{ACTION_TITLE[kind]}</h3>

      <form className="space-y-3" onSubmit={handleSubmit}>
        {mutation.isError && (
          <ErrorAlert title="Could not do that">{errorMessage(mutation.error)}</ErrorAlert>
        )}

        {kind === 'status' && (
          <Field
            label="New status"
            htmlFor="status"
            required
            error={errors['status']}
            hint="Only the moves this order's state machine allows are listed."
          >
            <Select
              id="status"
              required
              value={status}
              onChange={(event) => setStatus(event.target.value)}
            >
              {allowed.map((next) => (
                <option key={next} value={next}>
                  {ORDER_STATUS_LABEL[next]}
                </option>
              ))}
            </Select>
          </Field>
        )}

        {kind === 'cancel' && (
          <>
            <p className="rounded-lg bg-warning-50 px-3 py-2 text-xs text-warning-800">
              Cancelling releases the reserved stock and any deposit held against this order.
            </p>
            <Field label="Reason" htmlFor="reason" required error={errors['reason']}>
              <Textarea
                id="reason"
                rows={3}
                required
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                placeholder="Recorded on the order and shown to the buyer."
              />
            </Field>
          </>
        )}

        {kind === 'payment' && (
          <>
            <p className="rounded-lg bg-canvas px-3 py-2 text-xs text-ink-muted">
              {formatCurrency(outstanding)} outstanding. The order confirms itself once it is paid
              in full.
            </p>

            <Field label="Amount (₹)" htmlFor="amount" required error={errors['amount']}>
              <Input
                id="amount"
                type="number"
                step="0.01"
                required
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
              />
            </Field>

            <Field label="Method" htmlFor="method" error={errors['method']}>
              <Input
                id="method"
                value={method}
                onChange={(event) => setMethod(event.target.value)}
                placeholder="Bank transfer, cheque, UPI"
              />
            </Field>

            <Field label="Reference" htmlFor="reference" error={errors['reference']}>
              <Input
                id="reference"
                value={reference}
                onChange={(event) => setReference(event.target.value)}
                placeholder="UTR, cheque number or gateway reference"
              />
            </Field>
          </>
        )}

        {kind === 'shipment' && (
          <>
            <Field label="Carrier" htmlFor="carrier" required error={errors['carrier']}>
              <Input
                id="carrier"
                required
                value={carrier}
                onChange={(event) => setCarrier(event.target.value)}
                placeholder="Delhivery, Blue Dart, …"
              />
            </Field>

            <Field
              label="Tracking number"
              htmlFor="trackingNumber"
              error={errors['trackingNumber']}
            >
              <Input
                id="trackingNumber"
                value={trackingNumber}
                onChange={(event) => setTrackingNumber(event.target.value)}
              />
            </Field>

            <Field label="Tracking URL" htmlFor="trackingUrl" error={errors['trackingUrl']}>
              <Input
                id="trackingUrl"
                type="url"
                value={trackingUrl}
                onChange={(event) => setTrackingUrl(event.target.value)}
                placeholder="https://…"
              />
            </Field>

            <Field
              label="Estimated delivery"
              htmlFor="eta"
              error={errors['estimatedDeliveryDate']}
            >
              <Input
                id="eta"
                type="date"
                value={estimatedDeliveryDate}
                onChange={(event) => setEstimatedDeliveryDate(event.target.value)}
              />
            </Field>
          </>
        )}

        {kind !== 'cancel' && (
          <Field label="Notes" htmlFor="notes" error={errors['notes']}>
            <Textarea
              id="notes"
              rows={2}
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
            />
          </Field>
        )}

        <div className="flex gap-2 pt-1">
          <Button type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? 'Saving…' : 'Confirm'}
          </Button>
          <Button type="button" variant="outline" onClick={onDone} disabled={mutation.isPending}>
            Cancel
          </Button>
        </div>
      </form>
    </section>
  );
}
